import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router";
import {
  Loader2,
  Calendar,
  Users,
  Clock,
  ChevronDown,
  Edit,
  Trash2,
  Plus,
  Check,
  X,
  Info,
  LogOut,
} from "lucide-react";
import ReactDOM from "react-dom";

const SupervisorRequirement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSlotDropdownOpen, setIsSlotDropdownOpen] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDuplicateConfirmModal, setShowDuplicateConfirmModal] = useState(false);
  const [duplicateEntry, setDuplicateEntry] = useState(null);
  const [showRequirementModal, setShowRequirementModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [isHOD, setIsHOD] = useState(false);

  // HOD action states
  const [hodActionLoading, setHodActionLoading] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentApprovingId, setCurrentApprovingId] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [currentRejectingId, setCurrentRejectingId] = useState(null);
  const [tooltipData, setTooltipData] = useState({ show: false, reason: "", position: { x: 0, y: 0 } });

  // HOD edit/delete states
  const [hodEditingRecordId, setHodEditingRecordId] = useState(null);
  const [showHodEditReasonModal, setShowHodEditReasonModal] = useState(false);
  const [hodEditReason, setHodEditReason] = useState("");
  const [pendingHodEditData, setPendingHodEditData] = useState(null);
  const [showHodDeleteConfirmModal, setShowHodDeleteConfirmModal] = useState(false);
  const [hodDeleteReason, setHodDeleteReason] = useState("");
  const [currentHodDeletingId, setCurrentHodDeletingId] = useState(null);

  const [modalFormData, setModalFormData] = useState({
    daySelection: "single",
    date: new Date().toISOString().split("T")[0],
    dateRange: {
      from: new Date().toISOString().split("T")[0],
      to: new Date().toISOString().split("T")[0],
    },
    slot: "",
    numberOfWorkers: "",
    originalDate: "",
    originalSlot: "",
    originalWorkers: "",
  });

  const [modalFormErrors, setModalFormErrors] = useState({});
  const [historyData, setHistoryData] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [fetchingSlots, setFetchingSlots] = useState(false);

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const verifyTokenWithAuth = useCallback(
    async (authToken) => {
      try {
        const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        localStorage.setItem("department", response.data.user.department);
        localStorage.setItem("departmentId", response.data.user.departmentId);
        localStorage.setItem("companyId", response.data.user.MyCompany);
        setIsHOD(response.data.user.isHOD === true);
        return true;
      } catch (error) {
        console.error("Token verification failed:", error);
        localStorage.removeItem("floor_manager_token");
        return false;
      }
    },
    [BASE_URL]
  );

  const handleLogout = () => {
  if (window.confirm('Are you sure you want to logout?')) {
    localStorage.removeItem('floor_manager_token');
    navigate('/lkart/supervisor-login');
  }
};

  const isSlotUpcoming = (date, slotTime) => {
    const now = new Date();
    const slotDate = new Date(date);
    if (slotDate < new Date(now.toISOString().split("T")[0])) return false;
    if (slotDate > new Date(now.toISOString().split("T")[0])) return true;
    if (!slotTime) return false;
    // Extract time from format "Shift Name (HH:MM - HH:MM)"
    const timeMatch = slotTime.match(/\(([^)]+)\)/);
    const startTimeStr = timeMatch ? timeMatch[1].split(" - ")[0] : slotTime.split(" - ")[0];
    const [time, period] = startTimeStr.split(" ");
    let [hours, minutes] = time.split(":").map(Number);
    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    const slotStartTime = new Date(date);
    slotStartTime.setHours(hours, minutes, 0, 0);
    return slotStartTime >= new Date(now.getTime() - 2 * 60 * 60 * 1000);
  };

  const handleCreateRequirement = () => {
    setModalMode("create");
    setModalFormData({
      daySelection: "single",
      date: new Date().toISOString().split("T")[0],
      dateRange: {
        from: new Date().toISOString().split("T")[0],
        to: new Date().toISOString().split("T")[0],
      },
      slot: "",
      numberOfWorkers: "",
      originalDate: "",
      originalSlot: "",
      originalWorkers: "",
    });
    setModalFormErrors({});
    setShowRequirementModal(true);
  };

  const handleModify = (record) => {
    setModalMode("modify");
    setHodEditingRecordId(null);
    setModalFormData({
      daySelection: "single",
      date: record.date,
      dateRange: { from: record.date, to: record.date },
      slot: record.slot,
      numberOfWorkers: record.TotalWorkers,
      originalDate: record.date,
      originalSlot: record.slot,
      originalWorkers: record.TotalWorkers,
    });
    setModalFormErrors({});
    setShowRequirementModal(true);
  };

  // HOD-specific edit: open modal tagging the record id for HOD flow
  const handleHodEdit = (record) => {
    setModalMode("modify");
    setHodEditingRecordId(record._id);
    setModalFormData({
      daySelection: "single",
      date: record.date,
      dateRange: { from: record.date, to: record.date },
      slot: record.slot,
      numberOfWorkers: record.TotalWorkers,
      originalDate: record.date,
      originalSlot: record.slot,
      originalWorkers: record.TotalWorkers,
    });
    setModalFormErrors({});
    setShowRequirementModal(true);
  };

  const handleModalInputChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setModalFormData((prev) => ({
        ...prev,
        [parent]: { ...prev[parent], [child]: value },
      }));
    } else {
      setModalFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (modalFormErrors[field]) {
      setModalFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSlotSelect = (slot) => {
    handleModalInputChange("slot", slot);
    setIsSlotDropdownOpen(false);
  };

  const validateModalForm = () => {
    const errors = {};
    if (modalFormData.daySelection === "single") {
      if (!modalFormData.date) {
        errors.date = "Date is required";
      } else {
        const selectedDate = new Date(modalFormData.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) errors.date = "Cannot select previous dates";
      }
    } else {
      if (!modalFormData.dateRange.from || !modalFormData.dateRange.to) {
        errors.dateRange = "Both dates are required";
      } else {
        const fromDate = new Date(modalFormData.dateRange.from);
        const toDate = new Date(modalFormData.dateRange.to);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (fromDate < today || toDate < today)
          errors.dateRange = "Cannot select previous dates";
        if (fromDate > toDate)
          errors.dateRange = "End date must be after start date";
      }
    }
    if (!modalFormData.slot) errors.slot = "Slot is required";
    if (!modalFormData.numberOfWorkers) {
      errors.numberOfWorkers = "Number of workers is required";
    } else if (parseInt(modalFormData.numberOfWorkers) <= 0) {
      errors.numberOfWorkers = "Number of workers must be positive";
    }
    setModalFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!validateModalForm()) return;

    // If HOD is editing, capture form data and open reason modal
    if (isHOD && modalMode === "modify" && hodEditingRecordId) {
      setPendingHodEditData({ ...modalFormData });
      setHodEditReason("");
      setShowRequirementModal(false);
      setShowHodEditReasonModal(true);
      return;
    }

    if (modalMode === "create") {
      await handleCreateSubmit();
    } else {
      await handleModifySubmit();
    }
  };

  const handleCreateSubmit = async () => {
    if (modalFormData.daySelection === "single") {
      const isDuplicate = historyData.some(
        (record) =>
          record.date === modalFormData.date && record.slot === modalFormData.slot
      );
      if (isDuplicate) {
        const existingRecord = historyData.find(
          (record) =>
            record.date === modalFormData.date && record.slot === modalFormData.slot
        );
        setDuplicateEntry({
          message: "This slot already exists for the selected date",
          duplicates: [
            {
              date: modalFormData.date,
              slot: modalFormData.slot,
              existingWorkers: existingRecord?.TotalWorkers || 0,
            },
          ],
        });
        setShowDuplicateConfirmModal(true);
        return;
      }
    }
    await submitRequirement();
  };

  const handleModifySubmit = async () => {
    if (modalFormData.numberOfWorkers <= 0) {
      setErrorMessage("Worker count must be greater than 0");
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 2000);
      return;
    }
    if (!isSlotUpcoming(modalFormData.date, modalFormData.slot)) {
      setErrorMessage("Cannot modify to a completed time slot");
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 2000);
      return;
    }
    setLoading(true);
    try {
      const companyId = localStorage.getItem("companyId");
      const requestData = {
        originalDate: modalFormData.originalDate,
        originalSlot: modalFormData.originalSlot,
        newDate: modalFormData.date,
        slot: modalFormData.slot,
        TotalWorkers: parseInt(modalFormData.numberOfWorkers),
        companyId,
      };
      const response = await axios.put(
        `${BASE_URL}/floor-admin/update`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );
      if (response.data.success) {
        setShowSuccessModal(true);
        setErrorMessage("Entry updated successfully");
        fetchHistory();
        setShowRequirementModal(false);
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to update entry");
      }
    } catch (error) {
      console.error("Error updating requirement:", error);
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to update requirement"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  const submitRequirement = async () => {
    setLoading(true);
    try {
      const companyId = localStorage.getItem("companyId");
      const requestData =
        modalFormData.daySelection === "single"
          ? {
              isDateRange: false,
              startDate: modalFormData.date,
              slot: modalFormData.slot,
              TotalWorkers: parseInt(modalFormData.numberOfWorkers),
              companyId,
            }
          : {
              isDateRange: true,
              startDate: modalFormData.dateRange.from,
              endDate: modalFormData.dateRange.to,
              slot: modalFormData.slot,
              TotalWorkers: parseInt(modalFormData.numberOfWorkers),
              companyId,
            };

      const response = await axios.post(
        `${BASE_URL}/floor-admin/add`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );

      if (response.data.success) {
        setShowSuccessModal(true);
        setErrorMessage(
          modalFormData.daySelection === "single"
            ? "Requirement added successfully"
            : `${getDaysBetweenDates(
                modalFormData.dateRange.from,
                modalFormData.dateRange.to
              )} requirements added successfully`
        );
        fetchHistory();
        setShowRequirementModal(false);
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw response;
      }
    } catch (response) {
      if (response?.data?.error === "Duplicate entries found") {
        setDuplicateEntry({
          message: response.data.message,
          duplicates: response.data.duplicates,
        });
        setShowDuplicateConfirmModal(true);
      } else {
        setErrorMessage(
          response?.data?.message || response.data?.error || "Failed to send requirement"
        );
        setShowErrorModal(true);
        setTimeout(() => setShowErrorModal(false), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  const getDaysBetweenDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleDuplicateConfirm = async () => {
    setShowDuplicateConfirmModal(false);
    await submitRequirement();
  };

  const handleDeleteClick = (date, slot) => {
    setItemToDelete({ date, slot });
    setShowConfirmDialog(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      const companyId = localStorage.getItem("companyId");
      const response = await axios.delete(`${BASE_URL}/floor-admin/delete`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
        },
        data: { date: itemToDelete.date, slot: itemToDelete.slot, companyId },
      });
      if (response.data.success) {
        fetchHistory();
        setShowSuccessModal(true);
        setErrorMessage("Entry deleted successfully");
        setTimeout(() => setShowSuccessModal(false), 1000);
      } else {
        throw new Error(response.data.message || "Failed to delete entry");
      }
    } catch (error) {
      console.error("Error deleting requirement:", error);
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to delete requirement"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 2000);
    } finally {
      setDeleteLoading(false);
      setShowConfirmDialog(false);
      setItemToDelete(null);
    }
  };

  // ── HOD Action handlers ───────────────────────────────────────────────────

  const handleApproveClick = (requestId) => {
    setCurrentApprovingId(requestId);
    setShowApprovalModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!currentApprovingId) return;
    setHodActionLoading(currentApprovingId);
    setShowApprovalModal(false);
    try {
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        { requestId: currentApprovingId, action: "approve" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );
      if (response.data.success) {
        setErrorMessage("Request approved successfully");
        setShowSuccessModal(true);
        fetchHistory();
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to approve request");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to approve request"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setHodActionLoading(null);
      setCurrentApprovingId(null);
    }
  };

  const handleApproveCancel = () => {
    setShowApprovalModal(false);
    setCurrentApprovingId(null);
  };

  const handleRejectClick = (requestId) => {
    setCurrentRejectingId(requestId);
    setRejectionReason("");
    setShowRejectionModal(true);
  };

  const handleRejectConfirm = async () => {
    if (rejectionReason.trim().length < 3) {
      alert("Rejection reason must be at least 3 characters long");
      return;
    }
    if (rejectionReason.trim().length > 100) {
      alert("Rejection reason must not exceed 100 characters");
      return;
    }
    setHodActionLoading(currentRejectingId);
    setShowRejectionModal(false);
    try {
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        {
          requestId: currentRejectingId,
          action: "reject",
          reason: rejectionReason.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );
      if (response.data.success) {
        setErrorMessage("Request rejected successfully");
        setShowSuccessModal(true);
        fetchHistory();
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to reject request");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to reject request"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setHodActionLoading(null);
      setRejectionReason("");
      setCurrentRejectingId(null);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
    setCurrentRejectingId(null);
  };

  // ── HOD Edit handlers ─────────────────────────────────────────────────────

  const handleHodEditReasonConfirm = async () => {
    if (hodEditReason.trim().length < 3) {
      alert("Reason must be at least 3 characters long");
      return;
    }
    if (hodEditReason.trim().length > 100) {
      alert("Reason must not exceed 100 characters");
      return;
    }
    setHodActionLoading(hodEditingRecordId);
    setShowHodEditReasonModal(false);
    try {
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        {
  requestId: hodEditingRecordId,
  action: "edit",
  reason: hodEditReason.trim(),
  editedFields: {
    TotalWorkers: parseInt(pendingHodEditData.numberOfWorkers),
    slot: pendingHodEditData.slot,
    requestDate: new Date(pendingHodEditData.date).toISOString(),
  },
},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );
      if (response.data.success) {
        setErrorMessage("Entry updated successfully");
        setShowSuccessModal(true);
        fetchHistory();
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to update entry");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to update entry"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setHodActionLoading(null);
      setHodEditingRecordId(null);
      setPendingHodEditData(null);
      setHodEditReason("");
    }
  };

  const handleHodEditReasonCancel = () => {
    setShowHodEditReasonModal(false);
    setHodEditReason("");
    setPendingHodEditData(null);
    setHodEditingRecordId(null);
    // Re-open the edit modal so user doesn't lose their changes
    setShowRequirementModal(true);
  };

  // ── HOD Delete handlers ───────────────────────────────────────────────────

  const handleHodDeleteClick = (recordId) => {
    setCurrentHodDeletingId(recordId);
    setHodDeleteReason("");
    setShowHodDeleteConfirmModal(true);
  };

  const handleHodDeleteConfirm = async () => {
    if (hodDeleteReason.trim().length < 3) {
      alert("Reason must be at least 3 characters long");
      return;
    }
    if (hodDeleteReason.trim().length > 100) {
      alert("Reason must not exceed 100 characters");
      return;
    }
    setHodActionLoading(currentHodDeletingId);
    setShowHodDeleteConfirmModal(false);
    try {
      const response = await axios.post(
        `${BASE_URL}/floor-admin/hod-action`,
        {
          requestId: currentHodDeletingId,
          action: "delete",
          reason: hodDeleteReason.trim(),
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
          },
        }
      );
      if (response.data.success) {
        setErrorMessage("Entry deleted successfully");
        setShowSuccessModal(true);
        fetchHistory();
        setTimeout(() => setShowSuccessModal(false), 2000);
      } else {
        throw new Error(response.data.message || "Failed to delete entry");
      }
    } catch (error) {
      setErrorMessage(
        error.response?.data?.message || error.message || "Failed to delete entry"
      );
      setShowErrorModal(true);
      setTimeout(() => setShowErrorModal(false), 3000);
    } finally {
      setHodActionLoading(null);
      setCurrentHodDeletingId(null);
      setHodDeleteReason("");
    }
  };

  const handleHodDeleteCancel = () => {
    setShowHodDeleteConfirmModal(false);
    setHodDeleteReason("");
    setCurrentHodDeletingId(null);
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  const fetchTimeSlots = async (department, departmentId) => {
    if (!department) { setTimeSlots([]); return; }
    try {
      setFetchingSlots(true);
      const response = await fetch(`${BASE_URL}/lenskart/department-dropdown`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ department, departmentId }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.success && data.timeSlots) {
        const sortedSlots = data.timeSlots.sort(
          (a, b) =>
            new Date(`1970/01/01 ${a.start_time}`) -
            new Date(`1970/01/01 ${b.start_time}`)
        );
        setTimeSlots(sortedSlots.map((slot) => `${slot.shiftName} (${slot.slotStart} - ${slot.slotEnd})`));
      } else {
        setTimeSlots([]);
      }
    } catch (error) {
      console.error("Failed to fetch time slots:", error);
      setTimeSlots([]);
    } finally {
      setFetchingSlots(false);
    }
  };

  useEffect(() => {
    const checkTokenValidity = async () => {
      const token = localStorage.getItem("floor_manager_token");
      if (!token) {
        navigate("/lkart/supervisor-login");
        return;
      }
      const isTokenValid = await verifyTokenWithAuth(token);
      if (!isTokenValid) {
        navigate("/lkart/supervisor-login");
        return;
      }
      const department = localStorage.getItem("department");
      const departmentId = localStorage.getItem("departmentId");
      if (department && departmentId) {
        fetchTimeSlots(department, departmentId);
      }
      fetchHistory();
    };
    checkTokenValidity();
  }, [navigate, verifyTokenWithAuth]);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const companyId = localStorage.getItem("companyId");
      const departmentId = localStorage.getItem("departmentId");
      const response = await axios.get(`${BASE_URL}/floor-admin/department`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("floor_manager_token")}`,
        },
        params: { companyId, departmentId },
      });
      setHistoryData(response.data.data?.history || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".slot-dropdown")) {
        setIsSlotDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderSlotDropdownItems = (selectedDate, handleSelect) => (
    <>
      {timeSlots
        .filter((slot) => isSlotUpcoming(selectedDate, slot))
        .map((slot, index) => (
          <button
            key={`enabled-${index}`}
            type="button"
            onClick={() => handleSelect(slot)}
            className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
          >
            {slot}
          </button>
        ))}
      {timeSlots.some((slot) => !isSlotUpcoming(selectedDate, slot)) && (
        <div className="border-t border-gray-200 my-1" />
      )}
      {timeSlots
        .filter((slot) => !isSlotUpcoming(selectedDate, slot))
        .map((slot, index) => (
          <button
            key={`disabled-${index}`}
            type="button"
            disabled
            className="w-full px-3 py-2 text-left text-gray-400 cursor-not-allowed"
          >
            {slot} (Unavailable)
          </button>
        ))}
    </>
  );

  const colSpan = isHOD ? 7 : 6;

  return (
    <div className="min-h-screen overflow-y-auto pt-16 bg-gray-100 p-0">
      <div
        className={`max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-4 flex flex-col min-h-[calc(100vh-8rem)] ${
          isHOD ? "mt-10" : ""
        }`}
      >
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-bold">Worker Requirement</h2>
          {!isHOD && (
            <button
              onClick={handleCreateRequirement}
              className="px-6 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 flex items-center gap-2"
              style={{ backgroundColor: "#4F46E5" }}
            >
              <Plus className="h-4 w-4" />
              Submit Requirement
            </button>
          )}
        </div>
        <div className="mb-4">
          <span className="text-sm text-amber-700 font-medium">
  <span className="text-red-500">*</span> Slots starting within the last 2 hours or later can be selected
</span>
        </div>

        <div className="flex-1">
          <h3 className="text-lg font-semibold mb-4">Request History</h3>
          <div className="w-full h-[calc(100vh-12rem)] overflow-y-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white shadow-md z-10">
                <tr className="bg-gray-100">
                  <th className="p-3 text-center">Requested Date</th>
                  <th className="p-3 text-center">Requested Slot</th>
                  <th className="p-3 text-center">Total Workers</th>
                  <th className="p-3 text-left">Requested On</th>
<th className="p-3 text-center">Department</th>
<th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {historyLoading ? (
                  <tr>
                    <td colSpan={colSpan} className="text-center p-4">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : historyData.length > 0 ? (
                  historyData.map((record, index) => (
                    <tr
                      key={record._id || index}
                      className="border-b border-gray-200 hover:bg-gray-50"
                    >
                      <td className="p-3 text-center">{formatDate(record.date)}</td>
                      <td className="p-3 text-center">{record.slot || "-"}</td>
                      <td className="p-3 text-center">{record.TotalWorkers || "-"}</td>
                      <td className="p-3 text-left">{formatDateTime(record.created_at)}</td>
<td className="p-3 text-center">{record.adminDepartment || "-"}</td>

                      {/* Status cell */}
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
  record.status === "approved" || record.status?.toLowerCase() === "hr-approved"
    ? "bg-green-100 text-green-800"
    : record.status === "rejected" || record.status === "hod-rejected" || record.status?.toLowerCase() === "hr-rejected"
    ? "bg-red-100 text-red-800"
    : record.status === "sent-to-hod-approval"
    ? "bg-purple-100 text-purple-800"
    : record.status === "sent-to-hr-approval"
    ? "bg-blue-100 text-blue-800"
    : record.status?.toLowerCase() === "hod-edited-and-approved"
    ? "bg-teal-100 text-teal-800"
    : "bg-yellow-100 text-yellow-800"
}`}
                          >
                            {record.status === "sent-to-hod-approval"
  ? "Sent for HOD Approval"
  : record.status === "sent-to-hr-approval"
  ? "Sent for HR Approval"
  : record.status === "hod-rejected"
  ? "Rejected by HOD"
  : record.status?.toLowerCase() === "hr-approved"
  ? "Approved by HR"
  : record.status?.toLowerCase() === "hr-rejected"
  ? "Rejected by HR"
  : record.status?.toLowerCase() === "hod-edited-and-approved"
  ? "HOD Edited & Approved"
  : record.status
  ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
  : "Pending"}
                          </span>
                          {(record.status === "rejected" || record.status === "hod-rejected" || record.status?.toLowerCase() === "hr-rejected") && record.reason && (
                            <div
                              className="relative inline-block"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setTooltipData({
                                  show: true,
                                  reason: record.reason,
                                  position: { x: rect.left + rect.width / 2, y: rect.top },
                                });
                              }}
                              onMouseLeave={() =>
                                setTooltipData({ show: false, reason: "", position: { x: 0, y: 0 } })
                              }
                            >
                              <Info className="h-4 w-4 text-red-600 cursor-help" />
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Actions cell */}
                      <td className="p-3 text-center">
                        <div className="flex justify-center items-center space-x-2">
                          {hodActionLoading === record._id ? (
                            <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                          ) : isHOD ? (
                            <>
                              {/* Approve / Reject only for sent-to-hod-approval */}
                              {(["sent-to-hod-approval", "hod-edited-and-approved"].includes(record.status?.toLowerCase()) ||
  (record.status === "hod-rejected" && isSlotUpcoming(record.date, record.slot)) ||
  (record.status === "sent-to-hr-approval" && isSlotUpcoming(record.date, record.slot)) ||
  (record.status?.toLowerCase() === "hr-rejected" && isSlotUpcoming(record.date, record.slot))) && (
                                <>
                                {record.status !== "sent-to-hr-approval" && (
                                  <button
                                    onClick={() => handleApproveClick(record._id)}
                                    className="p-2 rounded-full bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
                                    title="Approve"
                                  >
                                    <Check className="h-4 w-4" />
                                  </button>
                                )}
                                {record.status !== "hod-rejected" && (
                                  <button
                                    onClick={() => handleRejectClick(record._id)}
                                    className="p-2 rounded-full bg-red-100 hover:bg-red-200 text-red-700 transition-colors"
                                    title="Reject"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                )}
                                </>
                              )}
                              {/* Edit — only for sent-to-hod-approval, sent-to-hr-approval, hod-rejected */}
                              {["sent-to-hod-approval", "hod-edited-and-approved"].includes(record.status?.toLowerCase()) ? (
                                <button
                                  onClick={() => handleHodEdit(record)}
                                  className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  disabled
                                  className="p-2 rounded-full text-gray-300 cursor-not-allowed"
                                  title="Edit (not available)"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              )}
                            </>
                          ) : (
                            // For Floor Admin (isHOD = false), only show edit and delete if the slot is upcoming
                            isSlotUpcoming(record.date, record.slot) && record.status === "sent-to-hod-approval" ? (
                              <>
                                <button
                                  onClick={() => handleModify(record)}
                                  className="text-blue-500 hover:text-blue-700 p-1 rounded-full hover:bg-blue-100 transition-colors"
                                  title="Modify"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(record.date, record.slot)}
                                  className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  className="text-gray-400 p-1 rounded-full cursor-not-allowed"
                                  title="Modify (disabled)"
                                  disabled
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  className="text-gray-400 p-1 rounded-full cursor-not-allowed"
                                  title="Delete (disabled)"
                                  disabled
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="text-center p-4 text-gray-500">
                      No history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-4 text-center">
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 hover:underline transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>

      {/* ── Submit / Modify Requirement Modal ─────────────────────────────── */}
      {showRequirementModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {modalMode === "create"
                ? "Submit Worker Requirement"
                : isHOD
                ? "Edit Worker Requirement (HOD)"
                : "Modify Worker Requirement"}
            </h3>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {modalMode === "create" && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Day Selection *
                  </label>
                  <div className="flex items-center space-x-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="daySelection"
                        className="form-radio h-4 w-4 text-blue-600"
                        checked={modalFormData.daySelection === "single"}
                        onChange={() => handleModalInputChange("daySelection", "single")}
                      />
                      <span className="ml-2">Single Day</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="daySelection"
                        className="form-radio h-4 w-4 text-blue-600"
                        checked={modalFormData.daySelection === "multiple"}
                        onChange={() => handleModalInputChange("daySelection", "multiple")}
                      />
                      <span className="ml-2">Multiple Days</span>
                    </label>
                  </div>
                </div>
              )}

              {isHOD && modalMode === "modify" && hodEditingRecordId ? (
  <>
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Calendar className="h-4 w-4 inline mr-1" />
        Date
      </label>
      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
        {formatDate(modalFormData.date)}
      </div>
    </div>

    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Clock className="h-4 w-4 inline mr-1" />
        Slot
      </label>
      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-700">
        {modalFormData.slot || "-"}
      </div>
    </div>

    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Users className="h-4 w-4 inline mr-1" />
        Number of Workers *
      </label>
      <input
        type="number"
        min="1"
        value={modalFormData.numberOfWorkers}
        onChange={(e) => handleModalInputChange("numberOfWorkers", e.target.value)}
        placeholder="Enter number"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          modalFormErrors.numberOfWorkers ? "border-red-500" : "border-gray-300"
        }`}
      />
      {modalFormErrors.numberOfWorkers && (
        <p className="text-red-500 text-xs mt-1">
          {modalFormErrors.numberOfWorkers}
        </p>
      )}
    </div>
  </>
) : (
  <>
    {modalFormData.daySelection === "single" || modalMode === "modify" ? (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          <Calendar className="h-4 w-4 inline mr-1" />
          {modalMode === "modify" ? "New Date" : "Date"} *
        </label>
        <input
          type="date"
          value={modalFormData.date}
          min={new Date().toISOString().split("T")[0]}
          onChange={(e) => {
            handleModalInputChange("date", e.target.value);
            if (
              e.target.value === new Date().toISOString().split("T")[0] &&
              modalFormData.slot
            ) {
              if (!isSlotUpcoming(e.target.value, modalFormData.slot)) {
                setErrorMessage("Selected slot has already completed");
                setShowErrorModal(true);
                setTimeout(() => setShowErrorModal(false), 2000);
                handleModalInputChange("slot", "");
              }
            }
          }}
          onClick={(e) => e.target.showPicker()}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            modalFormErrors.date ? "border-red-500" : "border-gray-300"
          }`}
        />
        {modalFormErrors.date && (
          <p className="text-red-500 text-xs mt-1">{modalFormErrors.date}</p>
        )}
      </div>
                //date range block
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <Calendar className="h-4 w-4 inline mr-1" />From Date *
                    </label>
                    <input
                      type="date"
                      value={modalFormData.dateRange.from}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        handleModalInputChange("dateRange.from", e.target.value);
                        if (
                          e.target.value === new Date().toISOString().split("T")[0] &&
                          modalFormData.slot
                        ) {
                          if (!isSlotUpcoming(e.target.value, modalFormData.slot)) {
                            setErrorMessage("Selected slot has already completed");
                            setShowErrorModal(true);
                            setTimeout(() => setShowErrorModal(false), 2000);
                            handleModalInputChange("slot", "");
                          }
                        }
                      }}
                      onClick={(e) => e.target.showPicker()}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        modalFormErrors.dateRange ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      <Calendar className="h-4 w-4 inline mr-1" />To Date *
                    </label>
                    <input
                      type="date"
                      value={modalFormData.dateRange.to}
                      min={modalFormData.dateRange.from}
                      onChange={(e) => handleModalInputChange("dateRange.to", e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        modalFormErrors.dateRange ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                  </div>
                  {modalFormErrors.dateRange && (
                    <p className="text-red-500 text-xs mt-1 col-span-2">
                      {modalFormErrors.dateRange}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2 slot-dropdown">
      <label className="block text-sm font-medium text-gray-700">
        <Clock className="h-4 w-4 inline mr-1" />
        {modalMode === "modify" ? "New Slot" : "Slot"} *
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsSlotDropdownOpen(!isSlotDropdownOpen)}
          className={`w-full h-[42px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between ${
            modalFormErrors.slot ? "border-red-500" : "border-gray-300"
          } ${!modalFormData.slot ? "text-gray-500" : "text-gray-900"}`}
        >
          <span>{modalFormData.slot || "Select Slot"}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              isSlotDropdownOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        {isSlotDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            {timeSlots.length > 0 ? (
              renderSlotDropdownItems(
                modalFormData.daySelection === "single" || modalMode === "modify"
                  ? modalFormData.date
                  : modalFormData.dateRange.from,
                handleSlotSelect
              )
            ) : (
              <div className="px-3 py-2 text-gray-500">
                {fetchingSlots ? "Loading slots..." : "No slots available"}
              </div>
            )}
          </div>
        )}
      </div>
      {modalFormErrors.slot && (
        <p className="text-red-500 text-xs mt-1">{modalFormErrors.slot}</p>
      )}
    </div>

    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        <Users className="h-4 w-4 inline mr-1" />
        {modalMode === "modify" ? "New Number of Workers" : "Number of Workers"} *
      </label>
      <input
        type="number"
        min="1"
        value={modalFormData.numberOfWorkers}
        onChange={(e) => handleModalInputChange("numberOfWorkers", e.target.value)}
        placeholder="Enter number"
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          modalFormErrors.numberOfWorkers ? "border-red-500" : "border-gray-300"
        }`}
      />
      {modalFormErrors.numberOfWorkers && (
        <p className="text-red-500 text-xs mt-1">
          {modalFormErrors.numberOfWorkers}
        </p>
      )}
    </div>
  </>
)}

              {modalMode === "modify" && (
                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Original Values:</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Date:</span>
                      <p className="font-medium">{formatDate(modalFormData.originalDate)}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Slot:</span>
                      <p className="font-medium">{modalFormData.originalSlot}</p>
                    </div>
                    <div>
                      <span className="text-gray-600">Workers:</span>
                      <p className="font-medium">{modalFormData.originalWorkers}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowRequirementModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    loading ||
                    !modalFormData.slot ||
                    (!isHOD &&
                      !isSlotUpcoming(
                        modalFormData.daySelection === "single" || modalMode === "modify"
                          ? modalFormData.date
                          : modalFormData.dateRange.from,
                        modalFormData.slot
                      ))
                  }
                  className={`px-6 py-2 text-white rounded-md flex items-center gap-2 ${
                    loading ||
                    !modalFormData.slot ||
                    (!isHOD &&
                      !isSlotUpcoming(
                        modalFormData.daySelection === "single" || modalMode === "modify"
                          ? modalFormData.date
                          : modalFormData.dateRange.from,
                        modalFormData.slot
                      ))
                      ? "bg-blue-300 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                  {modalMode === "create" ? "Submit Requirement" : "Update Requirement"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Dialog (regular supervisor) ────────────────────── */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-md w-full mx-4">
            <div className="text-blue-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this Slot?</p>
            <div className="flex justify-center space-x-4">
              <button onClick={() => setShowConfirmDialog(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleteLoading} className={`px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none flex items-center gap-2 ${deleteLoading ? "bg-red-300 cursor-not-allowed" : ""}`}>
                {deleteLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Duplicate Entry Modal ─────────────────────────────────────────── */}
      {showDuplicateConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4">
            <div className="text-yellow-500 mb-4 text-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2 text-center">Duplicate Entry Found</h3>
            <p className="text-sm text-gray-600 mb-4 text-center">{duplicateEntry?.message}</p>
            <div className="max-h-60 overflow-y-auto mb-4">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-2 text-left border">Date</th>
                    <th className="p-2 text-left border">Slot</th>
                    <th className="p-2 text-left border">Existing Workers</th>
                    <th className="p-2 text-center border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {duplicateEntry?.duplicates?.map((dup, index) => {
                    const existingRecord = historyData.find(
                      (record) => record.date === dup.date && record.slot === dup.slot
                    );
                    return (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="p-2 border">{formatDate(dup.date)}</td>
                        <td className="p-2 border">{dup.slot}</td>
                        <td className="p-2 border">{dup.existingWorkers}</td>
                        <td className="p-2 border text-center">
                          <button
                            onClick={() => {
                              if (existingRecord) {
                                setShowDuplicateConfirmModal(false);
                                handleModify(existingRecord);
                              }
                            }}
                            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none text-xs"
                          >
                            Modify
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Please modify the existing entry or choose a different slot/date.
            </p>
            <div className="flex justify-center space-x-4">
              <button onClick={() => setShowDuplicateConfirmModal(false)} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── HOD: Approval Confirmation Modal ─────────────────────────────── */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Confirm Approval</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to approve this request?</p>
            <div className="flex justify-end gap-3">
              <button onClick={handleApproveCancel} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={handleApproveConfirm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Approve</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HOD: Rejection Reason Modal ───────────────────────────────────── */}
      {showRejectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Rejection Reason</h3>
            <div className="mb-4">
              <label className="block text-sm text-gray-700 mb-2">
                Please provide a reason for rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => { if (e.target.value.length <= 100) setRejectionReason(e.target.value); }}
                placeholder="Enter rejection reason (min 3 characters, max 100 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows="4"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">{rejectionReason.length}/100</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={handleRejectCancel} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">Cancel</button>
              <button onClick={handleRejectConfirm} disabled={rejectionReason.trim().length < 3} className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors">Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HOD: Edit Reason Modal ────────────────────────────────────────── */}
      {showHodEditReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-1">Reason for Edit</h3>
            <p className="text-sm text-gray-500 mb-4">
              Please provide a reason for editing this entry.
            </p>
            <div className="mb-4">
              <label className="block text-sm text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={hodEditReason}
                onChange={(e) => { if (e.target.value.length <= 100) setHodEditReason(e.target.value); }}
                placeholder="Enter reason (min 3 characters, max 100 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows="4"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">{hodEditReason.length}/100</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={handleHodEditReasonCancel} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">Back</button>
              <button onClick={handleHodEditReasonConfirm} disabled={hodEditReason.trim().length < 3} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors">Confirm Update</button>
            </div>
          </div>
        </div>
      )}

      {/* ── HOD: Delete Confirm Modal ─────────────────────────────────────── */}
      {showHodDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-1">Confirm Deletion</h3>
            <p className="text-sm text-gray-500 mb-4">
              Please provide a reason for deleting this entry.
            </p>
            <div className="mb-4">
              <label className="block text-sm text-gray-700 mb-2">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={hodDeleteReason}
                onChange={(e) => { if (e.target.value.length <= 100) setHodDeleteReason(e.target.value); }}
                placeholder="Enter reason (min 3 characters, max 100 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                rows="4"
              />
              <div className="flex justify-end mt-1">
                <span className="text-xs text-gray-500">{hodDeleteReason.length}/100</span>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={handleHodDeleteCancel} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">Cancel</button>
              <button onClick={handleHodDeleteConfirm} disabled={hodDeleteReason.trim().length < 3} className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Success Modal ─────────────────────────────────────────────────── */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-md w-full mx-4">
            <div className="text-green-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900">{errorMessage}</h3>
          </div>
        </div>
      )}

      {/* ── Error Modal ───────────────────────────────────────────────────── */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 text-center max-w-md w-full mx-4">
            <div className="text-red-500 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
            <p className="text-sm text-gray-600">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* ── Tooltip Portal ────────────────────────────────────────────────── */}
      {tooltipData.show &&
        ReactDOM.createPortal(
          <div
            className="fixed px-3 py-2 bg-gray-900 text-white text-xs rounded-md shadow-lg whitespace-normal w-64 z-[9999]"
            style={{
              left: `${tooltipData.position.x}px`,
              top: `${tooltipData.position.y - 10}px`,
              transform: "translateX(-50%) translateY(-100%)",
              pointerEvents: "none",
            }}
          >
            <div className="font-semibold mb-1 text-gray-300">Rejection Reason:</div>
            <div className="text-gray-100 break-words">{tooltipData.reason}</div>
            <div
              className="absolute w-0 h-0"
              style={{
                bottom: "-6px",
                left: "50%",
                transform: "translateX(-50%)",
                borderLeft: "6px solid transparent",
                borderRight: "6px solid transparent",
                borderTop: "6px solid #111827",
              }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default SupervisorRequirement;